import { useCallback, useLayoutEffect, useRef, useState } from 'react';

export interface IPosition {
  top: number;
  left: number;
  width: number;
  maxHeight?: number;
}

interface IUseDropdownPosition {
  isOpen: boolean;
  triggerRef: React.RefObject<HTMLElement>;
  contentRef: React.RefObject<HTMLElement>;
  dropdownHeight: number;
  dynamic: boolean;
}

type Direction = 'top' | 'bottom';

const MARGIN = 16;
const OFFSET = 8;

export const useDropdownPosition = (
  options: IUseDropdownPosition,
  deps: unknown[] = []
) => {
  const [position, setPosition] = useState<IPosition | null>(null);
  const directionRef = useRef<Direction>('bottom');

  const updatePosition = useCallback(() => {
    if (!options.triggerRef.current || !options.contentRef.current) {
      return;
    }

    const triggerRect = options.triggerRef.current.getBoundingClientRect();
    const scrollY = window.scrollY;
    const scrollX = window.scrollX;
    const viewportHeight = window.innerHeight;

    const spaceAbove = triggerRect.top - MARGIN;
    const spaceBelow = viewportHeight - triggerRect.bottom - MARGIN;

    let direction = directionRef.current;
    const fitsBelow = options.dropdownHeight <= spaceBelow;
    const fitsAbove = options.dropdownHeight <= spaceAbove;

    if (direction === 'bottom' && !fitsBelow && fitsAbove) {
      direction = 'top';
    } else if (direction === 'top' && !fitsAbove && fitsBelow) {
      direction = 'bottom';
    } else if (!fitsBelow && !fitsAbove) {
      direction = spaceBelow >= spaceAbove ? 'bottom' : 'top';
    }

    directionRef.current = direction;

    let top: number;
    let maxHeight: number | undefined;

    if (direction === 'bottom') {
      top = triggerRect.bottom + OFFSET + scrollY;

      if (!fitsBelow) {
        maxHeight = spaceBelow;
      }
    } else {
      const heightToUse = fitsAbove ? options.dropdownHeight : spaceAbove;
      top = triggerRect.top + scrollY - heightToUse - OFFSET;

      if (!fitsAbove) {
        maxHeight = spaceAbove;
      }
    }

    const newPosition: IPosition = {
      top,
      left: triggerRect.left + scrollX,
      width: triggerRect.width,
      maxHeight,
    };

    setPosition((prev) =>
      prev &&
      prev.top === newPosition.top &&
      prev.left === newPosition.left &&
      prev.width === newPosition.width &&
      prev.maxHeight === newPosition.maxHeight
        ? prev
        : newPosition
    );
  }, [options.triggerRef, options.contentRef, ...deps]);

  useLayoutEffect(() => {
    if (!options.isOpen) {
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    updatePosition();

    window.addEventListener('resize', updatePosition, { signal });
    window.addEventListener('scroll', updatePosition, {
      capture: true,
      signal,
    });

    return () => {
      abortController.abort();
    };
  }, [options.isOpen, updatePosition]);

  return position;
};
